import { useState, useEffect } from 'react';
import { History, RotateCcw, Plus, Clock, User, Loader2, X, Eye, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const VersionHistoryModal = ({ 
  isOpen, 
  onClose, 
  whiteboardId, 
  token,
  onVersionRestored 
}) => {
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen && whiteboardId && whiteboardId !== 'new') {
      fetchVersions();
    }
  }, [isOpen, whiteboardId]);

  const fetchVersions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewVersion = async (version) => {
    setSelectedVersion(version);
    setIsLoadingPreview(true);
    setPreviewData(null);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/whiteboards/${whiteboardId}/versions/${version.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
      }
    } catch (error) {
      console.error('Error loading version preview:', error);
      toast.error('Failed to load version preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleRestoreVersion = async () => {
    if (!selectedVersion) return;
    
    setIsRestoring(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/whiteboards/${whiteboardId}/versions/${selectedVersion.id}/restore`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success(`Restored to version ${selectedVersion.version_number}`);
        onVersionRestored?.();
        onClose();
      } else {
        throw new Error('Failed to restore version');
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      toast.error('Failed to restore version');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCreateSnapshot = async () => {
    setIsCreating(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/whiteboards/${whiteboardId}/versions`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: null })
        }
      );

      if (response.ok) {
        toast.success('Snapshot created');
        fetchVersions();
      } else {
        throw new Error('Failed to create snapshot');
      }
    } catch (error) {
      console.error('Error creating snapshot:', error);
      toast.error('Failed to create snapshot');
    } finally {
      setIsCreating(false);
    }
  };

  // Simple SVG preview renderer
  const renderPreview = () => {
    if (!previewData || !previewData.objects) return null;

    // Calculate bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    previewData.objects.forEach(obj => {
      minX = Math.min(minX, obj.x || 0);
      minY = Math.min(minY, obj.y || 0);
      maxX = Math.max(maxX, (obj.x || 0) + (obj.width || 100));
      maxY = Math.max(maxY, (obj.y || 0) + (obj.height || 100));
    });

    const padding = 20;
    const width = maxX - minX + padding * 2;
    const height = maxY - minY + padding * 2;
    const scale = Math.min(300 / width, 200 / height, 1);

    return (
      <svg 
        width="100%" 
        height="200" 
        viewBox={`${minX - padding} ${minY - padding} ${width} ${height}`}
        className="bg-gray-50 rounded-lg border border-gray-200"
      >
        {previewData.objects.map((obj, idx) => {
          if (obj.type === 'shape' || obj.type === 'sticky') {
            return (
              <rect
                key={obj.id || idx}
                x={obj.x}
                y={obj.y}
                width={obj.width || 100}
                height={obj.height || 60}
                fill={obj.fill || obj.color || '#DBEAFE'}
                stroke={obj.stroke || '#3B82F6'}
                strokeWidth={1}
                rx={obj.type === 'sticky' ? 4 : 0}
              />
            );
          }
          if (obj.type === 'text') {
            return (
              <text
                key={obj.id || idx}
                x={obj.x}
                y={obj.y + 14}
                fontSize={10}
                fill={obj.textColor || '#1E293B'}
              >
                {obj.text?.slice(0, 20) || 'Text'}
              </text>
            );
          }
          return null;
        })}
      </svg>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Version History
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex gap-4 overflow-hidden">
          {/* Version List */}
          <div className="w-1/2 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">
                {versions.length} version{versions.length !== 1 ? 's' : ''}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCreateSnapshot}
                disabled={isCreating}
              >
                {isCreating ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <Plus size={14} className="mr-1" />
                )}
                Create Snapshot
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No versions yet</p>
                  <p className="text-sm mt-1">Versions are created automatically as you work</p>
                </div>
              ) : (
                versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => handlePreviewVersion(version)}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      selectedVersion?.id === version.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                    data-testid={`version-item-${version.version_number}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          version.name === 'Auto-save' 
                            ? 'bg-gray-100 text-gray-600' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {version.version_number}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-gray-900">
                            {version.name || `Version ${version.version_number}`}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span className="flex items-center gap-1">
                              <Clock size={10} />
                              {formatDate(version.created_at)}
                            </span>
                            <span>•</span>
                            <span>{version.object_count} objects</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                    <div className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                      <User size={10} />
                      {version.created_by_name}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Preview Panel */}
          <div className="w-1/2 flex flex-col border-l border-gray-200 pl-4">
            {selectedVersion ? (
              <>
                <div className="mb-3">
                  <h3 className="font-medium text-gray-900">
                    {selectedVersion.name || `Version ${selectedVersion.version_number}`}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {formatDate(selectedVersion.created_at)} by {selectedVersion.created_by_name}
                  </p>
                </div>

                <div className="flex-1 flex items-center justify-center">
                  {isLoadingPreview ? (
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  ) : previewData ? (
                    <div className="w-full">
                      {renderPreview()}
                      <p className="text-center text-xs text-gray-400 mt-2">
                        {previewData.objects?.length || 0} objects
                      </p>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <Eye size={24} className="mx-auto mb-2" />
                      <p className="text-sm">Preview unavailable</p>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={handleRestoreVersion}
                    disabled={isRestoring}
                  >
                    {isRestoring ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <RotateCcw size={16} className="mr-2" />
                    )}
                    Restore This Version
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Current state will be saved as a new version before restoring
                </p>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Eye size={32} className="mx-auto mb-2" />
                  <p>Select a version to preview</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
